import classNames from 'classnames'
import { dateFormats } from 'constants/common'
import formatFNS from 'date-fns/format'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { useContext, useMemo } from 'react'
import SlotAvailabilityIndicator from 'common/SlotAvailabilityIndicator'
import { slotAvailabilityEnumMapping } from 'app/Bookings/constants'
import useThriveAuth from 'hooks/useThriveAuth'
import styles from './index.module.scss'
import ScrollHorizontally from 'common/ScrollHorizontally'
import Button from 'react-bootstrap/Button'

type VerticalSlotGridProps = {
  providerDetails: Provider
  dates: Date[]
  generatedSlots: DateSlot
  selectedSlot: SelectedSlot
  onCardClick: (slot: ProviderSlot) => void
  showSlotSelection?: boolean
}
/**
 * Mobile responsive view of slots shown vertically
 */

const VerticalSlotGrid: React.FC<VerticalSlotGridProps> = ({
  dates,
  providerDetails,
  selectedSlot,
  generatedSlots,
  onCardClick,
  showSlotSelection = true,
}) => {
  const { format } = useContext(TimeZoneContext)
  const { isSupportOrThirdPartyUser } = useThriveAuth()
  const getSlots = (date) =>
    generatedSlots[formatFNS(date, dateFormats.YEAR_MONTH_DAY_FORMAT)]

  const isEmptySlots = (date) =>
    !(getSlots(date)?.filter((date) => !!date)?.length > 0)

  const { id: providerId } = providerDetails

  const providerHasNoAvailability = useMemo(
    () => dates.every((date) => isEmptySlots(date)),
    [dates, generatedSlots]
  )

  if (providerHasNoAvailability) {
    return (
      <div className="flex-fill flex-center pt-6 pb-5">
        <div
          className={`${styles.profile_no_slot} px-4 py-2 bg-light-blue text-muted-black`}
        >
          No slots available for this provider
        </div>
      </div>
    )
  }

  return (
    <>
      {dates.map((date, datesIndex) => {
        const slots = getSlots(date)

        if (isEmptySlots(date)) {
          return null
        }

        return (
          <div
            key={`mobile-col-${providerId}-${datesIndex}`}
            className="d-flex flex-column mb-4"
          >
            <div className="fs-16 mb-2">
              {formatFNS(
                new Date(date),
                dateFormats.FULL_WEEKDAY_MONTH_DAY_FORMAT
              )}
            </div>
            <ScrollHorizontally>
              <Row
                noGutters
                className="d-flex flex-nowrap"
                data-testid={`${providerDetails.id}-mob-slots`}
              >
                {slots?.map((slot, slotsIndex) => {
                  if (
                    slot?.start &&
                    slot?.practiceId === providerDetails?.practice?.id
                  ) {
                    const isSlotSelected = selectedSlot?.slot?.id === slot?.id

                    return (
                      <Col
                        key={`${providerId}-${datesIndex}-${slotsIndex}`}
                        className="d-flex flex-column align-items-stretch fs-14"
                      >
                        <Button
                          variant="outline-info"
                          key={`mobile-card-${providerId}`}
                          className={classNames(
                            'd-flex flex-column align-items-center text-center text-nowrap px-3 mr-3',
                            {
                              'item-selected':
                                isSlotSelected && showSlotSelection,
                            }
                          )}
                          onClick={() => onCardClick(slot)}
                          style={{
                            paddingTop: 11,
                            paddingBottom: 11,
                            borderRadius: 4,
                          }}
                        >
                          {format(
                            new Date(slot.start),
                            dateFormats.TIME_FORMAT
                          ).toLowerCase()}
                          {isSupportOrThirdPartyUser && (
                            <SlotAvailabilityIndicator
                              className="mt-1"
                              slotAvailabilities={slot?.availabilityType?.map(
                                (availability) =>
                                  slotAvailabilityEnumMapping[availability]
                              )}
                            />
                          )}
                        </Button>
                      </Col>
                    )
                  }
                })}
              </Row>
            </ScrollHorizontally>
          </div>
        )
      })}
    </>
  )
}

export default VerticalSlotGrid
